import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useNodeTypeRegistry, getCategoryColors } from './useNodeTypeRegistry';
import { useUnifiedCapabilities, clearUnifiedCache } from './useUnifiedCapabilities';
import QuickConnectModal from './QuickConnectModal';
import { useAgentCapabilities } from './useAgentCapabilities';

import {
  Lightning,
  WebhooksLogo,
  ArrowCircleRight,
  ArrowRight,
  ArrowsClockwise,
  Bell,
  BookOpen,
  Calculator,
  Calendar,
  CaretDown,
  CaretRight,
  ChatCircle,
  ChatText,
  CheckCircle,
  CircleNotch,
  Clock,
  Code,
  Cube,
  Database,
  DotsSixVertical,
  DownloadSimple,
  Envelope,
  Eye,
  FileCode,
  FileMagnifyingGlass,
  FileText,
  Funnel,
  GitBranch,
  Globe,
  Image,
  Intersect,
  Link,
  MagnifyingGlass,
  MapTrifold as MapIcon,
  MathOperations,
  Microphone,
  PaperPlaneRight,
  Phone,
  Play,
  Plug,
  Repeat,
  Robot,
  Scissors,
  Shield,
  Smiley,
  Sparkle,
  Stack,
  Table,
  Tag,
  Translate,
  UploadSimple,
  UsersThree,
  WarningOctagon,
  XCircle
} from '@phosphor-icons/react';
/**
 * NodePalette - Registry-driven draggable node sidebar
 * 
 * Features:
 * - Fetches node types from backend registry API
 * - 50+ node types across 11 categories
 * - Search/filter across all nodes
 * - Drag & drop to canvas
 * - Collapsible categories with counts
 */

// Icon mapping from registry icon names to Lucide components
const iconMap = {
  'play': Play,
  'webhook': WebhooksLogo,
  'clock': Clock,
  'bell': Bell,
  'message-square': ChatText,
  'file-text': FileText,
  'image': Image,
  'mic': Microphone,
  'zap': Lightning,
  'sparkles': Sparkle,
  'file-minus': FileText,
  'tag': Tag,
  'file-search': FileMagnifyingGlass,
  'smile': Smiley,
  'languages': Translate,
  'eye': Eye,
  'git-branch': GitBranch,
  'git-merge': GitBranch,
  'repeat': Repeat,
  'git-pull-request': GitBranch,
  'layers': Stack,
  'map': MapIcon,
  'filter': Funnel,
  'calculator': Calculator,
  'scissors': Scissors,
  'combine': Intersect,
  'file-code': FileCode,
  'variable': MathOperations,
  'globe': Globe,
  'mail': Envelope,
  'database': Database,
  'code': Code,
  'send': PaperPlaneRight,
  'shield': Shield,
  'alert-octagon': WarningOctagon,
  'refresh-cw': ArrowsClockwise,
  'slack': ChatText,
  'table': Table,
  'book-open': BookOpen,
  'message-circle': ChatCircle,
  'bot': Robot,
  'users': UsersThree,
  'arrow-right-circle': ArrowCircleRight,
  'monitor': FileText,
  'phone': Phone,
  'calendar': Calendar,
  'cube': Cube,
  'plug': Plug,
  'link': Link,
};

// Category display configuration
const categoryConfig = {
  triggers: { label: 'Triggers', icon: Lightning, order: 1 },
  inputs: { label: 'Inputs', icon: DownloadSimple, order: 2 },
  voice: { label: 'Voice Agent', icon: Microphone, order: 3 },
  ai: { label: 'AI & LLM', icon: Sparkle, order: 4 },
  agents: { label: 'Agent Chaining', icon: Robot, order: 5 },
  capabilities: { label: 'Capabilities', icon: Lightning, order: 6 },
  logic: { label: 'Logic & Flow', icon: GitBranch, order: 7 },
  transform: { label: 'Transform', icon: ArrowsClockwise, order: 8 },
  actions: { label: 'Actions', icon: Globe, order: 9 },
  error_handling: { label: 'Error Handling', icon: Shield, order: 10 },
  integrations: { label: 'Integrations', icon: Stack, order: 11 },
  outputs: { label: 'Outputs', icon: UploadSimple, order: 12 },
};

const NodePalette = ({ onNodeDragStart, onNodeAdd, isCollapsed, onToggle, agentId }) => {
  const { nodeTypes, categories, loading, error } = useNodeTypeRegistry();
  const { installedApps, integrations, summary: unifiedSummary, loading: unifiedLoading, refresh: refreshUnified } = useUnifiedCapabilities();
  const { capabilities: agentCapabilities, loading: agentCapsLoading, refresh: refreshAgentCaps } = useAgentCapabilities(agentId);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(agentId ? ['agent_capabilities'] : ['triggers']);
  const [connectingIntegration, setConnectingIntegration] = useState(null);
  const [recentlyConnected, setRecentlyConnected] = useState(new Set());

  // Accordion behavior - only one category open at a time
  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => 
      prev.includes(categoryKey) 
        ? [] // Close if already open
        : [categoryKey] // Open only this one (closes others)
    );
  };

  // Organize nodes by category from registry
  const organizedNodes = useMemo(() => {
    if (!nodeTypes) return {};
    
    const organized = {};
    
    Object.entries(nodeTypes).forEach(([typeId, nodeDef]) => {
      const category = nodeDef.category || 'other';
      if (!organized[category]) {
        const catConfig = categoryConfig[category] || { label: category, icon: Stack, order: 99 };
        const colors = getCategoryColors(category);
        organized[category] = {
          label: catConfig.label,
          icon: catConfig.icon,
          order: catConfig.order,
          ...colors,
          nodes: []
        };
      }
      
      organized[category].nodes.push({
        id: typeId,
        type: typeId,
        label: nodeDef.label,
        description: nodeDef.description,
        icon: iconMap[nodeDef.icon] || Lightning,
        params_schema: nodeDef.params_schema,
        default_inputs: nodeDef.default_inputs,
        default_outputs: nodeDef.default_outputs,
        supports_binding: nodeDef.supports_binding,
        can_have_side_effects: nodeDef.can_have_side_effects
      });
    });

    // Sort categories by order and nodes alphabetically within each
    const sortedOrganized = {};
    Object.entries(organized)
      .sort((a, b) => (a[1].order || 99) - (b[1].order || 99))
      .forEach(([key, value]) => {
        sortedOrganized[key] = {
          ...value,
          nodes: value.nodes.sort((a, b) => a.label.localeCompare(b.label))
        };
      });

    return sortedOrganized;
  }, [nodeTypes]);

  // Funnel nodes based on search
  const filteredCategories = useMemo(() => {
    // Start with registry nodes
    let allCategories = { ...organizedNodes };

    // ── Merge Agent-Attached Capabilities (highest priority, top of palette) ──
    if (agentId && agentCapabilities && agentCapabilities.length > 0) {
      const agentCapNodes = agentCapabilities.map(cap => ({
        id: cap.capability_type === 'integration' ? `connected.${cap.capability_id}` : `app.${cap.capability_id}`,
        type: cap.capability_type === 'integration' ? `connected.${cap.capability_id}` : `app.${cap.capability_id}`,
        label: cap.capability_name || cap.capability_id,
        description: cap.status === 'ready' ? 'Connected & ready' : cap.status === 'needs_connection' ? 'Needs connection' : 'Attached',
        icon: iconMap['plug'] || Plug,
        params_schema: {},
        default_inputs: [],
        default_outputs: [],
        supports_binding: true,
        can_have_side_effects: true,
        _source: cap.capability_type === 'integration' ? 'integration' : 'app_store',
        _connected: cap.auth === 'connected' || cap.auth === 'not_required',
        _provider_id: cap.capability_id,
        _health_status: cap.status,
        _auth_status: cap.auth,
        _actions: cap.actions || [],
      }));

      allCategories = {
        agent_capabilities: {
          label: 'Agent Capabilities',
          icon: Lightning,
          order: 0.1,
          bgColor: 'bg-cyan-500/10',
          color: 'text-cyan-400',
          borderColor: 'border-cyan-500/30',
          nodes: agentCapNodes,
        },
        ...allCategories,
      };
    }

    // ── Merge Installed Apps from Unified Registry ──
    if (installedApps && installedApps.length > 0) {
      const appNodes = installedApps.map(app => ({
        id: app.node_type,
        type: app.node_type,
        label: app.label,
        description: app.description,
        icon: iconMap[app.icon] || Cube,
        params_schema: app.params_schema,
        default_inputs: app.default_inputs,
        default_outputs: app.default_outputs,
        supports_binding: app.supports_binding,
        can_have_side_effects: app.can_have_side_effects,
        _source: 'app_store',
        _installed: true,
        _app_id: app.app_id,
      }));

      allCategories['installed_apps'] = {
        label: 'Installed Apps',
        icon: Cube,
        order: 0.5, // Before triggers
        bgColor: 'bg-emerald-500/10',
        color: 'text-emerald-400',
        borderColor: 'border-emerald-500/30',
        nodes: appNodes,
      };
    }

    // ── Merge Connected + Available Integrations from Unified Registry ──
    const connectedNodes = [];
    const disconnectedNodes = [];

    if (Array.isArray(integrations)) {
      for (const int of integrations) {
        const node = {
          id: int.node_type || `integration.${int.provider_id}`,
          type: int.node_type || `integration.${int.provider_id}`,
          label: int.label || int.provider_id || 'Integration',
          description: int.description || '',
          icon: iconMap[int.icon] || Plug,
          params_schema: int.params_schema || {},
          default_inputs: int.default_inputs || [],
          default_outputs: int.default_outputs || [],
          supports_binding: true,
          can_have_side_effects: true,
          _source: 'integration',
          _provider_id: int.provider_id,
        };

        if (int.connected) {
          connectedNodes.push({
            ...node,
            _connected: true,
            _connected_account: int.connected_account || '',
          });
        } else {
          disconnectedNodes.push({
            ...node,
            _connected: false,
            _settings_path: int.settings_path || '',
            connection_type: int.connection_type || '',
            connect_fields: int.connect_fields || [],
          });
        }
      }
    }

    if (connectedNodes.length > 0) {
      allCategories['connected_integrations'] = {
        label: 'Connected',
        icon: CheckCircle,
        order: 0.8,
        bgColor: 'bg-violet-500/10',
        color: 'text-violet-400',
        borderColor: 'border-violet-500/30',
        nodes: connectedNodes,
      };
    }

    if (disconnectedNodes.length > 0) {
      allCategories['available_integrations'] = {
        label: 'Available Integrations',
        icon: Plug,
        order: 13,
        bgColor: 'bg-slate-500/10',
        color: 'text-slate-400',
        borderColor: 'border-slate-500/30',
        nodes: disconnectedNodes,
      };
    }

    // Re-sort categories by order
    const sorted = {};
    Object.entries(allCategories)
      .sort((a, b) => (a[1].order || 99) - (b[1].order || 99))
      .forEach(([key, value]) => {
        sorted[key] = value;
      });

    if (!searchQuery.trim()) return sorted;

    const query = searchQuery.toLowerCase();
    const filtered = {};

    Object.entries(sorted).forEach(([key, category]) => {
      const matchingNodes = category.nodes.filter(node => 
        node.label.toLowerCase().includes(query) ||
        node.description?.toLowerCase().includes(query) ||
        node.id.toLowerCase().includes(query)
      );

      if (matchingNodes.length > 0) {
        filtered[key] = { ...category, nodes: matchingNodes };
      }
    });

    return filtered;
  }, [searchQuery, organizedNodes, installedApps, integrations, agentCapabilities, agentId]);

  const handleDragStart = (e, node, category) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      nodeType: node.type,
      category: category,
      label: node.label,
      params_schema: node.params_schema,
      default_inputs: node.default_inputs,
      default_outputs: node.default_outputs,
      _source: node._source || 'registry',
      _connected: node._connected,
      _provider_id: node._provider_id,
      _app_id: node._app_id,
    }));
    e.dataTransfer.effectAllowed = 'copy';
    onNodeDragStart?.(node, category);
  };

  // Handle click on disconnected integration -> open quick-connect modal
  const handleNodeClick = (node, category) => {
    if (node._source === 'integration' && !node._connected) {
      // Open inline quick-connect modal, passing full node data + provider_id
      setConnectingIntegration({
        ...node,
        provider_id: node._provider_id,
      });
      return;
    }
    onNodeAdd?.(node, category);
  };

  // Handle successful connection from QuickConnectModal
  const handleConnected = useCallback((providerId, data) => {
    setConnectingIntegration(null);
    // Mark as recently connected for highlight animation
    setRecentlyConnected(prev => new Set([...prev, providerId]));
    // Clear highlight after 3 seconds
    setTimeout(() => {
      setRecentlyConnected(prev => {
        const next = new Set(prev);
        next.delete(providerId);
        return next;
      });
    }, 3000);
    // Refresh unified capabilities cache immediately
    clearUnifiedCache();
    refreshUnified();
  }, [refreshUnified]);

  // Collapsed state
  if (isCollapsed) {
    return (
      <div className="w-12 h-full bg-[var(--surface-1)] border-r border-[var(--border-default)] flex flex-col items-center py-4">
        <button
          onClick={onToggle}
          className="p-2 rounded-lg hover:bg-[var(--surface-3)] text-[var(--text-muted)] hover:text-white transition-colors"
          title="Expand node palette"
        >
          <CaretRight size={20} />
        </button>
        
        <div className="mt-4 space-y-2 overflow-y-auto flex-1">
          {Object.entries(organizedNodes).slice(0, 8).map(([key, category]) => {
            const Icon = category.icon;
            return (
              <button
                key={key}
                className={`p-2 rounded-lg ${category.bgColor} ${category.color} hover:brightness-125 transition-all`}
                title={`${category.label} (${category.nodes.length})`}
                onClick={() => {
                  onToggle?.();
                  setExpandedCategories([key]);
                }}
              >
                <Icon size={18} />
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="w-64 h-full bg-[var(--surface-1)] border-r border-[var(--border-default)] flex items-center justify-center">
        <div className="text-center">
          <CircleNotch className="w-6 h-6 text-indigo-400 animate-spin mx-auto mb-2" />
          <p className="text-xs text-[var(--text-muted)]">Loading nodes...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-64 h-full bg-[var(--surface-1)] border-r border-[var(--border-default)] flex items-center justify-center p-4">
        <div className="text-center">
          <WarningOctagon className="w-6 h-6 text-red-400 mx-auto mb-2" />
          <p className="text-xs text-red-400">Failed to load nodes</p>
          <p className="text-[10px] text-[var(--text-muted)] mt-1">{error}</p>
        </div>
      </div>
    );
  }

  const totalNodes = Object.values(organizedNodes).reduce((sum, cat) => sum + cat.nodes.length, 0);

  return (
    <>
    <div className="w-64 h-full bg-[var(--surface-1)] border-r border-[var(--border-default)] flex flex-col" data-testid="node-palette">
      {/* Header */}
      <div className="p-4 border-b border-[var(--border-default)]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Stack size={16} className="text-indigo-400" />
            Nodes
            <span className="text-xs font-normal text-[var(--text-muted)]">({totalNodes})</span>
          </h3>
          <button
            onClick={onToggle}
            className="p-1.5 rounded-lg hover:bg-[var(--surface-3)] text-[var(--text-muted)] hover:text-white transition-colors"
          >
            <CaretDown size={16} className="rotate-90" />
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Search nodes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-md bg-[var(--surface-4)] border border-[var(--border-default)]
              text-sm text-white placeholder-[var(--text-muted)] focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/30"
            data-testid="node-search"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {Object.entries(filteredCategories).map(([key, category]) => {
          const isExpanded = expandedCategories.includes(key);
          const CategoryIcon = category.icon;

          return (
            <div key={key} className="rounded-lg overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(key)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md
                  hover:bg-[var(--surface-3)] transition-colors text-left`}
                data-testid={`category-${key}`}
              >
                <div className={`p-1.5 rounded-md ${category.bgColor}`}>
                  <CategoryIcon size={14} className={category.color} />
                </div>
                <span className="text-sm font-medium text-white flex-1">
                  {category.label}
                </span>
                <span className="text-xs text-[var(--text-muted)] mr-1">
                  {category.nodes.length}
                </span>
                <CaretRight 
                  size={14} 
                  className={`text-[var(--text-muted)] transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                />
              </button>

              {/* Nodes */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="pl-2 pr-1 pb-2 space-y-1">
                      {category.nodes.map((node) => {
                        const NodeIcon = node.icon;
                        const isDisconnected = node._source === 'integration' && !node._connected;
                        const isUnified = node._source === 'app_store' || node._source === 'integration';
                        const isJustConnected = recentlyConnected.has(node._provider_id);
                        const isAgentCap = !!node._health_status;
                        const healthStatus = node._health_status;

                        return (
                          <div
                            key={node.id}
                            draggable={!isDisconnected}
                            onDragStart={(e) => !isDisconnected && handleDragStart(e, node, key)}
                            onClick={() => handleNodeClick(node, key)}
                            className={`group flex items-center gap-2 px-2 py-2 rounded-md
                              border transition-all
                              ${isJustConnected
                                ? 'bg-emerald-500/10 border-emerald-500/40 ring-1 ring-emerald-400/30'
                                : isDisconnected 
                                  ? 'bg-[var(--surface-2)]/30 border-[var(--border-default)] opacity-60 cursor-pointer hover:opacity-80 hover:border-violet-500/30' 
                                  : 'bg-[var(--surface-3)]/50 border-[var(--border-default)] hover:bg-[var(--surface-4)] cursor-grab active:cursor-grabbing hover:border-[var(--border-strong)]'
                              }`}
                            data-testid={`node-${node.id.replace(/\./g, '-')}`}
                            title={isDisconnected ? `Click to connect ${node.label}` : node.description}
                          >
                            {!isDisconnected && (
                              <DotsSixVertical 
                                size={12} 
                                className="text-[var(--text-muted)] opacity-40 group-hover:opacity-80 flex-shrink-0" 
                              />
                            )}
                            <div className={`p-1.5 rounded-md ${category.bgColor} flex-shrink-0 relative`}>
                              <NodeIcon size={12} className={category.color} />
                              {/* Health status indicator for agent capabilities */}
                              {isAgentCap && healthStatus === 'ready' && (
                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 border border-[var(--surface-1)]" title="Connected & healthy" />
                              )}
                              {isAgentCap && healthStatus === 'needs_connection' && (
                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 border border-[var(--surface-1)]" title="Needs connection" />
                              )}
                              {isAgentCap && healthStatus === 'unknown' && (
                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-slate-400 border border-[var(--surface-1)]" title="Status unknown" />
                              )}
                              {/* Connection status dot (non-agent-cap) */}
                              {!isAgentCap && node._source === 'integration' && node._connected && (
                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 border border-[var(--surface-1)]" />
                              )}
                              {!isAgentCap && isDisconnected && (
                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-slate-500 border border-[var(--surface-1)]" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs font-medium truncate ${isDisconnected ? 'text-[var(--text-muted)]' : 'text-white'}`}>
                                {node.label}
                              </p>
                              <p className="text-[10px] text-[var(--text-muted)] truncate">
                                {isDisconnected ? 'Click to connect' : (node._connected_account || node.description)}
                              </p>
                            </div>
                            {/* Badges */}
                            <div className="flex gap-1 items-center">
                              {isDisconnected && (
                                <Link size={10} className="text-slate-500" />
                              )}
                              {!isDisconnected && node.can_have_side_effects && (
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" title="Can have side effects" />
                              )}
                              {!isDisconnected && node.supports_binding && !isUnified && (
                                <span className="w-1.5 h-1.5 rounded-full bg-purple-400" title="Supports provider binding" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        {/* No results */}
        {Object.keys(filteredCategories).length === 0 && (
          <div className="text-center py-8">
            <MagnifyingGlass className="w-8 h-8 text-[var(--text-muted)] mx-auto mb-2" />
            <p className="text-sm text-[var(--text-muted)]">No nodes found</p>
            <p className="text-xs text-[var(--text-muted)] opacity-60">Try a different search term</p>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="p-3 border-t border-[var(--border-default)]">
        {agentCapabilities && agentCapabilities.length > 0 ? (
          <div className="flex items-center justify-center gap-3 text-[10px] text-[var(--text-muted)]">
            <span className="flex items-center gap-1">
              <Lightning size={10} className="text-cyan-400" />
              {agentCapabilities.length} attached
            </span>
            {agentCapabilities.filter(c => c._health_status === 'ready').length > 0 && (
              <span className="flex items-center gap-1">
                <CheckCircle size={10} className="text-emerald-400" />
                {agentCapabilities.filter(c => c._health_status === 'ready').length} ready
              </span>
            )}
          </div>
        ) : unifiedSummary && (unifiedSummary.total_installed_apps > 0 || unifiedSummary.connected_integrations > 0) ? (
          <div className="flex items-center justify-center gap-3 text-[10px] text-[var(--text-muted)]">
            {unifiedSummary.total_installed_apps > 0 && (
              <span className="flex items-center gap-1">
                <Cube size={10} className="text-emerald-400" />
                {unifiedSummary.total_installed_apps} app{unifiedSummary.total_installed_apps !== 1 ? 's' : ''}
              </span>
            )}
            {unifiedSummary.connected_integrations > 0 && (
              <span className="flex items-center gap-1">
                <CheckCircle size={10} className="text-violet-400" />
                {unifiedSummary.connected_integrations} connected
              </span>
            )}
          </div>
        ) : (
          <p className="text-[10px] text-[var(--text-muted)] text-center">
            Drag nodes to canvas or click to add
          </p>
        )}
      </div>
    </div>

    {/* Quick-Connect Modal */}
    {connectingIntegration && (
      <QuickConnectModal
        integration={connectingIntegration}
        onClose={() => setConnectingIntegration(null)}
        onConnected={handleConnected}
      />
    )}
    </>
  );
};

// Legacy export for backward compatibility
export const nodeDefinitions = {};

export default NodePalette;
